/**
 * Schémas Zod pour les thèmes contextuels.
 *
 * Utilisés à toutes les frontières :
 *   - validation des inputs des edge functions (themes-create, themes-moderate…)
 *   - validation des forms côté apps/app
 *   - parsing des réponses Supabase pour les composants critiques
 *
 * Note : la regex slug est calquée sur le CHECK constraint SQL (cf. migration
 * 0083_themes.sql). Toute modification doit être faite des deux côtés.
 */

import { z } from 'zod';

// ── Enums ────────────────────────────────────────────────────────────────────

export const ThemeVisibilitySchema = z.enum(['private', 'pending_review', 'public']);
export type ThemeVisibility = z.infer<typeof ThemeVisibilitySchema>;

export const ThemeSensitivitySchema = z.enum(['all', 'sensitive', 'mature']);
export type ThemeSensitivity = z.infer<typeof ThemeSensitivitySchema>;

export const ThemeStatusSchema = z.enum(['draft', 'published', 'archived']);
export type ThemeStatus = z.infer<typeof ThemeStatusSchema>;

export const SchoolLevelSchema = z.enum([
  '6e',
  '5e',
  '4e',
  '3e',
  '2nde',
  '1re',
  'tle',
  'mpsi',
  'ptsi',
  'mp',
  'pt',
]);

// ── Validation du slug ───────────────────────────────────────────────────────
// Doit refléter le CHECK constraint SQL : ^[a-z0-9]+(-[a-z0-9]+)*$ + longueur.
const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const ThemeSlugSchema = z
  .string()
  .min(2, 'Slug trop court (2 caractères minimum)')
  .max(80, 'Slug trop long (80 caractères maximum)')
  .regex(SLUG_REGEX, 'Slug invalide : uniquement lettres minuscules, chiffres et tirets');

// ── Lecture (forme stockée en base) ──────────────────────────────────────────

export const ThemeSchema = z.object({
  id: z.string().uuid(),
  slug: ThemeSlugSchema,
  title: z.string().min(1).max(120),
  short_description: z.string().max(300),
  description_md: z.string().max(20000),
  illustration_url: z.string().url().nullable(),
  min_level: SchoolLevelSchema.nullable(),
  sensitivity: ThemeSensitivitySchema,
  topical_until: z.string().nullable(), // date ISO 'YYYY-MM-DD'
  status: ThemeStatusSchema,
  visibility: ThemeVisibilitySchema,
  author_id: z.string().uuid(),
  exercise_count: z.number().int().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
  published_at: z.string().nullable(),
});

export type Theme = z.infer<typeof ThemeSchema>;

// ── Création (input edge function themes-create) ─────────────────────────────
// L'auteur fournit le contenu. Le slug est généré côté serveur depuis le titre
// (et peut être édité plus tard via Update). La visibilité est forcée à
// 'private' côté serveur (les policies l'imposent aussi).

export const CreateThemeInputSchema = z.object({
  title: z.string().trim().min(1, 'Titre requis').max(120, 'Titre trop long (120 max)'),
  short_description: z
    .string()
    .trim()
    .max(300, 'Description courte trop longue (300 max)')
    .default(''),
  description_md: z.string().max(20000, 'Description trop longue (20000 max)').default(''),
  illustration_url: z.string().url("URL d'illustration invalide").nullable().optional(),
  min_level: SchoolLevelSchema.nullable().optional(),
  sensitivity: ThemeSensitivitySchema.default('all'),
  topical_until: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Format attendu : YYYY-MM-DD')
    .nullable()
    .optional(),
});

export type CreateThemeInput = z.infer<typeof CreateThemeInputSchema>;

// ── Mise à jour ──────────────────────────────────────────────────────────────
// Tous les champs sont optionnels. Le slug peut être édité ici (avec validation
// du format). La visibilité, le status et published_at sont modifiables par les
// edge functions dédiées uniquement.

export const UpdateThemeInputSchema = z.object({
  slug: ThemeSlugSchema.optional(),
  title: z.string().trim().min(1).max(120).optional(),
  short_description: z.string().trim().max(300).optional(),
  description_md: z.string().max(20000).optional(),
  illustration_url: z.string().url().nullable().optional(),
  min_level: SchoolLevelSchema.nullable().optional(),
  sensitivity: ThemeSensitivitySchema.optional(),
  topical_until: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  status: ThemeStatusSchema.optional(),
});

export type UpdateThemeInput = z.infer<typeof UpdateThemeInputSchema>;

// ── Modération (admin only) ──────────────────────────────────────────────────

export const ModerateThemeInputSchema = z.object({
  themeId: z.string().uuid(),
  action: z.enum(['approve', 'reject']),
  reason: z.string().max(500).optional(),
});

export type ModerateThemeInput = z.infer<typeof ModerateThemeInputSchema>;

// ── Soumission à review (auteur) ─────────────────────────────────────────────

export const SubmitThemeForReviewInputSchema = z.object({
  themeId: z.string().uuid(),
});

export type SubmitThemeForReviewInput = z.infer<typeof SubmitThemeForReviewInputSchema>;
